import { DashboardCard } from "@/components/ui/DashboardCard/DashboardCard";
import { HStack } from "@/components/layout/HStack";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table/Table";
import { Badge } from "@/components/ui/Badge/Badge";
import { Button } from "@/components/ui/Button/Button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/Tooltip/Tooltip";
import { CircleMinus } from "lucide-react";
import { format } from "date-fns";
import type {
  ApiScheduledEmail,
  ScheduledEmailStatus,
} from "@shared/types/models";

const statusVariant: Record<
  ScheduledEmailStatus,
  "default" | "success" | "destructive" | "secondary"
> = {
  pending: "default",
  sending: "default",
  sent: "success",
  failed: "destructive",
  canceled: "secondary",
};

const statusLabel: Record<ScheduledEmailStatus, string> = {
  pending: "Pending",
  sending: "Sending",
  sent: "Sent",
  failed: "Failed",
  canceled: "Canceled",
};

const audienceLabel = (audience: ApiScheduledEmail["audience"]) => {
  switch (audience.type) {
    case "all-students":
      return "All students";
    default:
      return audience.type;
  }
};

const ScheduledEmailsTable = ({
  data,
  onCancel,
}: {
  data: ApiScheduledEmail[];
  onCancel: (id: string) => void;
}) => {
  return (
    <DashboardCard>
      <Table borderColor="var(--secondary-border)">
        <TableHeader>
          <TableRow>
            <TableHead>Status</TableHead>
            <TableHead>Subject</TableHead>
            <TableHead>Audience</TableHead>
            <TableHead>Send at</TableHead>
            <TableHead style={{ textAlign: "right" }}>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5}>No scheduled emails</TableCell>
            </TableRow>
          ) : (
            data.map((email) => (
              <TableRow key={email._id}>
                <TableCell>
                  <Badge variant={statusVariant[email.status]}>
                    {statusLabel[email.status]}
                  </Badge>
                </TableCell>
                <TableCell>{email.subject}</TableCell>
                <TableCell>{audienceLabel(email.audience)}</TableCell>
                <TableCell>
                  {format(new Date(email.sendAt), "MMM dd, yyyy 'at' h:mm a")}
                </TableCell>
                <TableCell>
                  <HStack justify="flex-end" gap="0.25rem">
                    {email.status === "pending" ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            size="icon-small"
                            variant="destructive"
                            onClick={() => onCancel(email._id)}
                          >
                            <CircleMinus />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Cancel email</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </HStack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </DashboardCard>
  );
};

export { ScheduledEmailsTable };
